/**
 * axios配置：可自行根据项目进行更改，只需更改该文件即可，本文件内其他文件勿动
 * 参考：https://doc.vvbin.cn/guide/mock.html#index-ts-%E9%85%8D%E7%BD%AE%E8%AF%B4%E6%98%8E
 */
import type { AxiosResponse } from 'axios'
import type { RequestOptions, RequestResult } from '@radical/types'
import type { AxiosTransform, CreateAxiosOptions } from './axiosTransform'

import { VAxios } from './Axios'
import { checkStatus } from './checkStatus'
import { context } from './bridge'
import { useI18n } from '@radical/locale'
import {
  isString,
  isFunction,
  clone,
  deepMerge,
  appendUrlParams,
} from '@radical/utils'
import { RequestEnum, ResultEnum, ContentTypeEnum } from './constants'
import { joinTimestamp, formatRequestDate, handleErrorFunction } from './helper'
import { useUserStoreWithout } from '@/store/user'

/**
 * @description: 数据处理，方便区分多种处理方式
 */
const transform: AxiosTransform = {
  /**
   * @description: 处理请求数据。如果数据不是预期格式，可直接抛出错误
   */
  transformRequestHook: (
    res: AxiosResponse<RequestResult>,
    options: RequestOptions,
  ) => {
    const {
      isTransformResponse,
      isDeconstructionData,
      isReturnNativeResponse,
    } = options
    // 是否返回原生响应头 比如：需要获取响应头时使用该属性
    if (isReturnNativeResponse) {
      return res
    }
    const { t } = useI18n()
    // 不进行任何处理，直接返回：用于页面代码可能需要直接获取code，data，message这些信息时开启
    if (!isTransformResponse) {
      return res.data
    }
    // 错误的时候返回
    const { data } = res
    if (!data) {
      throw new Error(t('sys.api.apiRequestFailed'))
    }
    //  这里 code，data，msg为 后台统一的字段，需要在 types.ts内修改 RequestResult 为项目自己的接口返回格式
    const { code, data: result, message, total } = data
    // 这里逻辑可以根据项目进行修改
    const hasSuccess =
      data && Reflect.has(data, 'code') && code === ResultEnum.SUCCESS

    if (hasSuccess) {
      if (isDeconstructionData) {
        if (total) result['total'] = total
        return result
      }
      return { data: result, total }
    }
    // 在此处根据自己项目的实际情况对不同的code执行不同的操作
    // 如果不希望中断当前请求，请return数据，否则直接抛出异常即可
    let timeoutMsg = ''
    switch (code) {
      case ResultEnum.TIMEOUT:
        timeoutMsg = t('sys.api.timeoutMessage')
        const router = context.router
        // 这里不做返回之前的页面逻辑了，比较复杂
        router.replace({
          name: 'Login',
          query: {
            redirect: encodeURIComponent(router.currentRoute.value.fullPath),
          },
        })
        break
      default:
        if (message) {
          timeoutMsg = message
        }
    }
    /**
     * modal：会显示modal错误弹窗，而不是消息提示，用于一些比较重要的错误
     * none： 一般是调用时明确表示不希望自动弹出错误提示
     */
    if (timeoutMsg && options.errorMessageMode) {
      handleErrorFunction(timeoutMsg, options.errorMessageMode)
    }
    throw new Error(timeoutMsg || t('sys.api.apiRequestFailed'))
  },

  // 请求之前处理config
  beforeRequestHook: (config, options) => {
    const {
      apiUrl,
      joinParamsToUrl,
      formatDate,
      joinTime = true,
      withToken = true,
    } = options

    if (apiUrl) {
      const _apuUrl = isString(apiUrl)
        ? apiUrl
        : isFunction(apiUrl)
        ? apiUrl?.()
        : ''
      config.url = `${_apuUrl}${config.url}`
    }
    // Add JWT token to headers
    if (withToken) {
      const userStore = useUserStoreWithout()
      const token = userStore.getToken
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        }
      } else {
        const router = context.router
        router.replace({
          name: 'Login',
        })
      }
    }

    const params = config.params || {}
    const data = config.data || false
    formatDate && data && !isString(data) && formatRequestDate(data)
    if (config.method?.toUpperCase() === RequestEnum.GET) {
      if (!isString(params)) {
        // 给 get 请求加上时间戳参数，避免从缓存中拿数据。
        config.params = Object.assign(
          params || {},
          joinTimestamp(joinTime, false),
        )
      } else {
        // 兼容restful风格
        config.url = config.url + params + `${joinTimestamp(joinTime, true)}`
        config.params = undefined
      }
    } else {
      if (!isString(params)) {
        formatDate && formatRequestDate(params)
        if (
          Reflect.has(config, 'data') &&
          config.data &&
          (Object.keys(config.data).length > 0 ||
            // 兼容空数组的情况
            Array.isArray(config.data))
        ) {
          config.data = data
          config.params = params
        } else {
          // 非GET请求如果没有提供data，则将params视为data
          config.data = params
          config.params = undefined
        }
        if (joinParamsToUrl) {
          config.url = appendUrlParams(
            config.url as string,
            Object.assign({}, config.params, config.data),
          )
        }
      } else {
        // 兼容restful风格
        config.url = config.url + params
        config.params = undefined
      }
    }

    return config
  },

  /**
   * @description: 响应拦截器处理
   */
  responseInterceptors: (res: AxiosResponse<any>) => {
    // 检查响应头中是否有New-Token
    const newToken = res.headers?.['New-Token']
    if (newToken) {
      const userStore = useUserStoreWithout()
      // 使用New-Token更新当前token
      userStore.setToken(newToken)
    }
    return res
  },

  /**
   * @description: 响应错误处理
   */
  responseInterceptorsCatch: (error: any) => {
    const { t } = useI18n()
    const { response, code, message, config } = error || {}
    const errorMessageMode = config?.requestOptions?.errorMessageMode || 'none'
    const msg: string = response?.data?.error?.message ?? ''
    const err: string = error?.toString?.() ?? ''
    let errMessage = ''

    try {
      if (code === 'ECONNABORTED' && message.indexOf('timeout') !== -1) {
        errMessage = t('sys.api.apiTimeoutMessage')
      }
      if (err?.includes('Network Error')) {
        errMessage = t('sys.api.networkExceptionMsg')
      }
      // 其他错误处理
      if (errMessage && errorMessageMode) {
        handleErrorFunction(errMessage, errorMessageMode)
        return Promise.reject(error)
      }
    } catch (error) {
      throw new Error(error as unknown as string)
    }

    checkStatus(error?.response?.status, msg, errorMessageMode)
    return Promise.reject(error)
  },
}

export const createAxios = (opt?: Partial<CreateAxiosOptions>) => {
  return new VAxios(
    deepMerge(
      {
        // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#authentication_schemes
        // authentication schemes，e.g: Bearer
        // authenticationScheme: 'Bearer',
        authenticationScheme: '',
        timeout: 0,
        // 基础接口地址

        headers: { 'Content-Type': ContentTypeEnum.JSON },
        // 如果是form-data格式
        // headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
        // 数据处理方式
        transform: clone(transform),
        // 配置项，下面的选项都可以在独立的接口请求中覆盖
        requestOptions: {
          // 是否返回原生响应头 比如：需要获取响应头时使用该属性
          isReturnNativeResponse: false,
          // 需要对返回数据进行处理
          isTransformResponse: true,
          // 是否解构data这个返回字段，默认解构，gb则不解构
          isDeconstructionData: true,
          // post请求的时候添加参数到url
          joinParamsToUrl: false,
          // 格式化提交参数时间
          formatDate: true,
          // 消息提示类型
          errorMessageMode: 'message',
          // 接口地址
          apiUrl: () => getRequestBaseUrl('gb28181'),
          //  是否加入时间戳
          joinTime: false,
          // 忽略重复请求
          ignoreCancelToken: true,
          // 是否携带token
          withToken: true,
        },
      },
      opt || {},
    ),
  )
}
export function getRequestBaseUrl(
  pluginName = '',
  prefix = '/api',
  protocol = 'http',
) {
  if (pluginName) pluginName = '/' + pluginName
  const host = (
    import.meta.env.VITE_M7S_SERVER || `${location.protocol}//${location.host}`
  ).replace(/^http/, protocol)
  return `${host}${pluginName}${prefix}`
}
export const http = createAxios({
  requestOptions: {
    // 接口地址
    apiUrl: getRequestBaseUrl,
  },
})
